import {
  accountSummarySchema,
  authStateSchema,
  CSRF_HEADER,
  CSRF_VALUE,
  sessionListSchema,
  type AccountSummary,
  type AuthState,
  type SessionItem,
} from '@personal-os/shared';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export class AuthApiError extends Error {
  readonly status: number;
  /** One of AUTH_ERROR_CODES when the server sent one. */
  readonly code: string | undefined;
  /** When a lockout ends (ISO), for "locked" errors. */
  readonly lockedUntil: string | undefined;

  constructor(status: number, code: string | undefined, lockedUntil?: string) {
    super(code ?? `request failed (${status})`);
    this.name = 'AuthApiError';
    this.status = status;
    this.code = code;
    this.lockedUntil = lockedUntil;
  }
}

async function request(method: string, path: string, body?: unknown): Promise<unknown> {
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`/api/auth${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const record = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
    throw new AuthApiError(
      res.status,
      typeof record.error === 'string' ? record.error : undefined,
      typeof record.lockedUntil === 'string' ? record.lockedUntil : undefined,
    );
  }
  return json;
}

const post = (path: string, body?: unknown) => request('POST', path, body ?? {});

interface Ceremony<O> {
  challengeId: string;
  options: O;
}
type RegistrationCeremony = Ceremony<PublicKeyCredentialCreationOptionsJSON>;
type AuthenticationCeremony = Ceremony<PublicKeyCredentialRequestOptionsJSON>;

// ---- state and account ----
export const getAuthState = async (): Promise<AuthState> =>
  authStateSchema.parse(await request('GET', '/state'));
export const getAccount = async (): Promise<AccountSummary> =>
  accountSummarySchema.parse(await request('GET', '/account'));
export const getSessions = async (): Promise<SessionItem[]> =>
  sessionListSchema.parse(await request('GET', '/sessions')).sessions;

// ---- first-run setup ----
export const setupOptions = async (setupToken: string) =>
  (await post('/setup/options', { setupToken })) as RegistrationCeremony;
export const setupVerify = (
  setupToken: string,
  challengeId: string,
  response: RegistrationResponseJSON,
) => post('/setup/verify', { setupToken, challengeId, response });

// ---- sign in ----
export const loginOptions = async () => (await post('/login/options')) as AuthenticationCeremony;
export const loginVerify = (challengeId: string, response: AuthenticationResponseJSON) =>
  post('/login/verify', { challengeId, response });
export const loginAuthenticator = (code: string) => post('/login/authenticator', { code });
export const loginRecovery = async (code: string) =>
  (await post('/login/recovery', { code })) as { ok: true; remaining: number };

// ---- sign out and devices ----
export const logout = () => post('/logout');
export const logoutAll = () => post('/logout-all');
export const revokeSession = (id: string) => request('DELETE', `/sessions/${id}`);

// ---- re-check ----
export const stepupOptions = async () => (await post('/stepup/options')) as AuthenticationCeremony;
export const stepupVerify = (challengeId: string, response: AuthenticationResponseJSON) =>
  post('/stepup/verify', { challengeId, response });
export const stepupAuthenticator = (code: string) => post('/stepup/authenticator', { code });

// ---- passkeys ----
export const passkeyOptions = async () => (await post('/passkeys/options')) as RegistrationCeremony;
export const passkeyAdd = (
  challengeId: string,
  response: RegistrationResponseJSON,
  label?: string,
) => post('/passkeys/verify', { challengeId, response, ...(label ? { label } : {}) });
export const passkeyRename = (id: string, label: string) =>
  request('PATCH', `/passkeys/${id}`, { label });
export const passkeyRemove = (id: string) => request('DELETE', `/passkeys/${id}`);

// ---- authenticator app and recovery codes ----
export const totpStart = async () => (await post('/totp/start')) as { secret: string; uri: string };
export const totpConfirm = async (code: string) =>
  (await post('/totp/confirm', { code })) as { ok: true; recoveryCodes: string[] | null };
export const totpDisable = () => request('DELETE', '/totp');
export const regenerateRecoveryCodes = async () =>
  (await post('/recovery/regenerate')) as { ok: true; recoveryCodes: string[] };
